import { useEffect, useState } from 'react';
import axios from 'axios';
import { ArrowLeft, Download, Loader2, X } from 'lucide-react';
import { downloadReturnBatch } from './returnBatchDownload';

// Use the configured default Axios client so auth/refresh interceptors run.
const getBatch = (url, extra = {}) => axios.get(url, {
  baseURL: process.env.REACT_APP_API_URL,
  withCredentials: true,
  ...extra,
});
const message = error => error.response?.data?.error || 'No se pudieron cargar las devoluciones.';
const limaTime = value => value ? new Date(value).toLocaleString('es-PE', { timeZone: 'America/Lima', dateStyle: 'medium', timeStyle: 'short' }) : '—';
const ownerLabel = batch => `${batch.scope === 'provider' ? 'Proveedor' : 'Tienda'}: ${batch.store_name}`;

export default function ReturnBatchesModal({ isOpen, onClose }) {
  const [date, setDate] = useState('');
  const [page, setPage] = useState(1);
  const [listing, setListing] = useState(null);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen || selected) return undefined;
    let active = true;
    setLoading(true);
    setListing(null);
    setError('');
    getBatch('/api/return-batches', { params: { page, ...(date ? { date } : {}) } })
      .then(({ data }) => { if (active) setListing(data); })
      .catch(err => { if (active) setError(message(err)); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [isOpen, page, date, selected]);

  if (!isOpen) return null;

  const close = () => {
    setSelected(null);
    setDate('');
    setPage(1);
    setListing(null);
    setError('');
    onClose();
  };

  const openBatch = async id => {
    setLoading(true);
    setError('');
    try {
      const { data } = await getBatch(`/api/return-batches/${id}`);
      setSelected(data);
    } catch (err) {
      setError(message(err));
    } finally {
      setLoading(false);
    }
  };

  const download = async id => {
    setDownloading(true);
    setError('');
    try {
      await downloadReturnBatch(id);
    } catch (err) {
      setError(message(err));
    } finally {
      setDownloading(false);
    }
  };

  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true" aria-label="Devoluciones">
    <div className="flex max-h-[90vh] w-full max-w-5xl flex-col rounded-2xl bg-white shadow-xl">
      <div className="flex min-w-0 items-center justify-between border-b p-3 sm:p-5">
        <div className="flex min-w-0 items-center gap-3">
          {selected && <button type="button" onClick={() => setSelected(null)} aria-label="Volver a lotes"><ArrowLeft size={19} /></button>}
          <div><h2 className="text-lg font-bold text-slate-800">{selected ? `Lote #${selected.id}` : 'Devoluciones'}</h2>
            <p className="text-sm text-slate-500">{selected ? `${ownerLabel(selected)} · ${limaTime(selected.created_at)}` : 'Lotes recientes, ordenados por fecha de preparación'}</p></div>
        </div>
        <button type="button" onClick={close} aria-label="Cerrar" className="rounded-lg p-2 hover:bg-slate-100"><X size={20} /></button>
      </div>
      <div className="overflow-auto p-3 sm:p-5">
        {error && <p role="alert" className="mb-3 rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</p>}
        {loading && <Loader2 className="animate-spin text-[#B1336E]" />}
        {!selected && <>
          <div className="mb-4 flex flex-wrap items-center gap-3"><label htmlFor="returns-date" className="text-sm font-semibold text-slate-600">Fecha de preparación</label>
            <input id="returns-date" type="date" value={date} onChange={event => { setDate(event.target.value); setPage(1); }} className="rounded-lg border px-3 py-2 text-sm" />
            {date && <button type="button" onClick={() => { setDate(''); setPage(1); }} className="text-sm text-[#B1336E]">Ver recientes</button>}
          </div>
          {!loading && listing?.batches.length === 0 && <p className="text-sm text-slate-500">No hay lotes para esta selección.</p>}
          <div className="space-y-2">{listing?.batches.map(batch => <button key={batch.id} type="button" onClick={() => openBatch(batch.id)} className="flex min-w-0 w-full flex-wrap items-center justify-between gap-3 rounded-xl border p-4 text-left hover:border-[#B1336E] hover:bg-slate-50">
            <div><strong className="text-slate-800">Lote #{batch.id} · {ownerLabel(batch)}</strong><p className="text-sm text-slate-500">{limaTime(batch.created_at)}</p></div>
            <span className="text-sm font-semibold text-[#B1336E]">{batch.handed_over_count}/{batch.package_count} entregados a tienda</span>
          </button>)}</div>
          <div className="mt-4 flex justify-end gap-3 text-sm"><button type="button" disabled={page <= 1} onClick={() => setPage(page - 1)}>Anterior</button><span>{page}</span><button type="button" disabled={!listing?.pagination?.has_next} onClick={() => setPage(page + 1)}>Siguiente</button></div>
        </>}
        {selected && <>
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3"><p className="text-sm font-semibold text-slate-600">{selected.handed_over_count} de {selected.package_count} entregados a tienda (reportado por conductor)</p>
            <button type="button" disabled={downloading} onClick={() => download(selected.id)} className="inline-flex items-center gap-2 rounded-lg bg-[#B1336E] px-4 py-2 text-sm font-bold text-white disabled:opacity-50"><Download size={17} /> Descargar Excel</button>
          </div>
          <div className="overflow-x-auto"><table className="w-full min-w-[920px] text-left text-sm"><thead className="bg-slate-50 text-xs uppercase text-slate-500"><tr><th className="p-3">Entrega</th><th className="p-3">Tienda</th><th className="p-3">Destinatario</th><th className="p-3">Teléfono</th><th className="p-3">Notas del conductor</th><th className="p-3">Estado de entrega</th><th className="p-3">Entregado a tienda</th></tr></thead>
            <tbody className="divide-y">{selected.items.map(item => <tr key={item.id}><td className="p-3">#{item.delivery_point_id} {item.delivery_type === 'provincia' ? '(Provincia)' : ''}</td><td className="p-3">{item.store_name || '—'}</td><td className="p-3">{item.recipient_name || '—'}</td><td className="p-3">{item.recipient_phone || '—'}</td><td className="max-w-xs whitespace-pre-wrap p-3">{item.driver_notes || '—'}</td><td className="p-3">{item.delivery_status || '—'}</td><td className="p-3">{item.handed_over_at ? limaTime(item.handed_over_at) : 'Pendiente'}</td></tr>)}</tbody>
          </table></div>
        </>}
      </div>
    </div>
  </div>;
}
